import { createFileRoute, useRouter } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import type { DateRange } from 'react-day-picker';

const RevenueChart = lazy(() => import('@/components/dashboard/RevenueChart'));
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { dateKeyInTimeZone, formatDateTimeInTimeZone } from '@/lib/date-values';
import { OwnerHome } from '@/components/dashboard/OwnerHome';
import { DashboardKpi } from '@/components/dashboard/DashboardKpi';
import { DashboardViewToggle } from '@/components/dashboard/DashboardViewToggle';
import {
  Briefcase,
  Receipt,
  CalendarDays,
  AlertCircle,
  RefreshCw,
  FileText,
  Clock,
  CheckSquare,
  ArrowRight,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Calendar } from '@/components/atoms/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  useDashboardStats,
  type DashboardStats,
  type DateFilter,
} from '@/hooks/dashboard/useDashboardStats';
import { useUpcomingJobs } from '@/hooks/dashboard/useUpcomingJobs';
import { useActionNeeded, type ActionItemType } from '@/hooks/dashboard/useActionNeeded';
import { usePipelineStats, type PipelineStats } from '@/hooks/dashboard/usePipelineStats';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@/lib/api';
import { useTodayTasks } from '@/hooks/tasks/useTodayTasks';
import {
  TASK_PRIORITY_BADGE_CLASS,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_DOT_CLASS,
  isTaskComplete,
  isTaskOverdue,
  taskWorkflowStatus,
} from '@/lib/tasks/taskModel';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import { Fullbleed } from '@/contexts/fullbleed';
import { useDashboardHeader } from '@/contexts/dashboard-header';
import { useAuthEmail } from '@/stores/auth.store';
import { useMyProfile } from '@/hooks/useMyProfile';
import { greetingFirstName } from '@/lib/users/greetingName';
import { readDateRangeSearch, replaceDateRangeSearch } from '@/lib/date-range-search';

export const Route = createFileRoute('/_dashboard/dashboard/home')({
  component: DashboardHomePage,
});

// ─── Header period control ───────────────────────────────────────────────────

type HomePeriodChoice =
  | 'today'
  | 'last_7_days'
  | 'last_30_days'
  | 'month_to_date'
  | 'year_to_date'
  | 'custom';

const HOME_PERIODS: Array<{ value: HomePeriodChoice; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'year_to_date', label: 'Year to date' },
  { value: 'custom', label: 'Custom range' },
];

function shiftDateKey(dateKey: string, days: number) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function startOfYearKey(dateKey: string) {
  return `${dateKey.slice(0, 4)}-01-01`;
}

function startOfMonthKey(dateKey: string) {
  return `${dateKey.slice(0, 7)}-01`;
}

function startOfWeekKey(dateKey: string) {
  const weekday = new Date(`${dateKey}T00:00:00Z`).getUTCDay();
  return shiftDateKey(dateKey, -(weekday === 0 ? 6 : weekday - 1));
}

function dateFromKey(dateKey: string) {
  return new Date(`${dateKey}T00:00:00`);
}

function dateKeyFromLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function selectedHomePeriod(filter: DateFilter, todayKey: string): HomePeriodChoice {
  if (filter.period === 'today') return 'today';
  if (filter.period === 'this_month') return 'month_to_date';
  if (
    filter.period === 'custom' &&
    filter.startDate === shiftDateKey(todayKey, -6) &&
    filter.endDate === todayKey
  ) {
    return 'last_7_days';
  }
  if (filter.period !== 'custom' || filter.endDate !== todayKey) return 'custom';
  if (filter.startDate === shiftDateKey(todayKey, -29)) return 'last_30_days';
  if (filter.startDate === startOfMonthKey(todayKey)) return 'month_to_date';
  if (filter.startDate === startOfYearKey(todayKey)) return 'year_to_date';
  return 'custom';
}

function homePeriodLabel(filter: DateFilter, todayKey: string) {
  const selected = selectedHomePeriod(filter, todayKey);
  if (selected !== 'custom') {
    return HOME_PERIODS.find((period) => period.value === selected)?.label ?? 'Last 7 days';
  }
  if (!filter.startDate || !filter.endDate) return 'Custom range';
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  return `${formatter.format(dateFromKey(filter.startDate))} – ${formatter.format(
    dateFromKey(filter.endDate),
  )}`;
}

function homeFilterFromSearch(todayKey: string): DateFilter {
  const value = readDateRangeSearch();
  if (!value) {
    return {
      period: 'custom',
      startDate: shiftDateKey(todayKey, -29),
      endDate: todayKey,
    };
  }
  if (value.preset === 'today') return { period: 'today' };
  if (value.preset === 'custom' && value.from && value.to) {
    return { period: 'custom', startDate: value.from, endDate: value.to };
  }
  const startDate = {
    today: undefined,
    week: startOfWeekKey(todayKey),
    '7d': shiftDateKey(todayKey, -6),
    '30d': shiftDateKey(todayKey, -29),
    mtd: startOfMonthKey(todayKey),
    ytd: startOfYearKey(todayKey),
    custom: undefined,
  }[value.preset];
  return startDate ? { period: 'custom', startDate, endDate: todayKey } : { period: 'today' };
}

function persistHomeFilter(filter: DateFilter, todayKey: string) {
  const selected = selectedHomePeriod(filter, todayKey);
  const preset = {
    today: 'today',
    last_7_days: '7d',
    last_30_days: '30d',
    month_to_date: 'mtd',
    year_to_date: 'ytd',
    custom: 'custom',
  }[selected] as 'today' | '7d' | '30d' | 'mtd' | 'ytd' | 'custom';
  replaceDateRangeSearch(
    preset === 'custom' && filter.startDate && filter.endDate
      ? { preset, from: filter.startDate, to: filter.endDate }
      : { preset },
  );
}

function HomePeriodControl({
  filter,
  todayKey,
  onChange,
}: {
  filter: DateFilter;
  todayKey: string;
  onChange: (filter: DateFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();
  const selected = selectedHomePeriod(filter, todayKey);
  const label = homePeriodLabel(filter, todayKey);

  const selectPeriod = (period: HomePeriodChoice) => {
    if (period === 'custom') {
      setShowCustom(true);
      return;
    }
    if (period === 'today') {
      onChange({ period: 'today' });
      setOpen(false);
      return;
    }
    const startDate = {
      last_7_days: shiftDateKey(todayKey, -6),
      last_30_days: shiftDateKey(todayKey, -29),
      month_to_date: startOfMonthKey(todayKey),
      year_to_date: startOfYearKey(todayKey),
    }[period];
    onChange({
      period: 'custom',
      startDate,
      endDate: todayKey,
    });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftRange?.from || !draftRange.to) return;
    onChange({
      period: 'custom',
      startDate: dateKeyFromLocalDate(draftRange.from),
      endDate: dateKeyFromLocalDate(draftRange.to),
    });
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) return;
        setShowCustom(selected === 'custom');
        setDraftRange(
          filter.startDate || filter.endDate
            ? {
                from: filter.startDate ? dateFromKey(filter.startDate) : undefined,
                to: filter.endDate ? dateFromKey(filter.endDate) : undefined,
              }
            : undefined,
        );
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 min-w-[126px] justify-between gap-2">
          <span className="flex items-center gap-2">
            <CalendarDays className="size-3.5 text-muted-foreground" />
            {label}
          </span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={cn('p-2', showCustom ? 'w-auto' : 'w-56')}>
        {showCustom ? (
          <>
            <div className="flex items-center justify-between px-1 pb-2">
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setShowCustom(false)}
              >
                Presets
              </button>
              <span className="text-xs text-muted-foreground">Choose start and end dates</span>
            </div>
            <Calendar
              mode="range"
              selected={draftRange}
              onSelect={setDraftRange}
              numberOfMonths={1}
              disabled={{ after: dateFromKey(todayKey) }}
            />
            <div className="flex justify-end border-t px-1 pt-2">
              <Button
                size="sm"
                disabled={!draftRange?.from || !draftRange.to}
                onClick={applyCustom}
              >
                Apply range
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-0.5">
            {HOME_PERIODS.map((period) => (
              <button
                type="button"
                key={period.value}
                className={cn(
                  'flex w-full items-center rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted',
                  selected === period.value && 'bg-muted font-medium',
                )}
                onClick={() => selectPeriod(period.value)}
              >
                {period.label}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
// ─── Widget error / skeleton ─────────────────────────────────────────────────

function WidgetError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
      <AlertCircle className="h-8 w-8 text-destructive" />
      <p className="text-sm text-muted-foreground">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

// ─── Action Needed ───────────────────────────────────────────────────────────

const ACTION_ICONS: Record<ActionItemType, React.ReactNode> = {
  invoice: <Receipt className="h-4 w-4" />,
  estimate: <FileText className="h-4 w-4" />,
  job: <Briefcase className="h-4 w-4" />,
};

const URGENCY_STYLES: Record<string, string> = {
  high: 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950',
  medium: 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950',
  low: '',
};

function ActionNeeded() {
  const query = useActionNeeded();
  const router = useRouter();

  if (!query.hasAccess) return null;

  return (
    <Card data-testid="widget-action-needed">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          Needs Attention
        </CardTitle>
      </CardHeader>
      <CardContent>
        {query.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        )}
        {query.isError && (
          <WidgetError message="Failed to load action items" onRetry={() => query.refetch()} />
        )}
        {query.data && query.data.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
            <CheckSquare className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">You're all caught up!</p>
          </div>
        )}
        {query.data && query.data.length > 0 && (
          <>
            {/* Mobile: horizontal scroll */}
            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 md:hidden">
              {query.data.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`flex min-w-[240px] snap-start items-start gap-3 rounded-lg border p-3 text-left transition-colors active:bg-muted ${URGENCY_STYLES[item.urgency] ?? ''}`}
                  onClick={() => void router.navigate({ to: item.link })}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    {ACTION_ICONS[item.type]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                  </div>
                </button>
              ))}
            </div>
            {/* Desktop: vertical list */}
            <ul className="hidden md:block space-y-1.5">
              {query.data.slice(0, 6).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted ${URGENCY_STYLES[item.urgency] ?? ''}`}
                    onClick={() => void router.navigate({ to: item.link })}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      {ACTION_ICONS[item.type]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Upcoming Jobs ───────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  scheduled: 'default',
  confirmed: 'default',
  pending: 'secondary',
  cancelled: 'destructive',
  completed: 'outline',
};

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  return STATUS_VARIANT[status.toLowerCase()] ?? 'secondary';
}

function UpcomingJobs() {
  const timeZone = useCompanyTimezone();
  const query = useUpcomingJobs(timeZone);
  const router = useRouter();

  return (
    <Card data-testid="widget-upcoming">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            Upcoming Jobs
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-muted-foreground"
            onClick={() =>
              void router.navigate({
                to: '/dashboard/calendar',
                search: {
                  view: 'week',
                  date: timeZone
                    ? dateKeyInTimeZone(new Date().toISOString(), timeZone)
                    : undefined,
                  tasks: true,
                  bookingId: undefined,
                },
              })
            }
          >
            View all
            <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className="space-y-1 flex-1">
                  <Skeleton className="h-3.5 w-1/2" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        )}
        {query.isError && (
          <WidgetError
            message={query.error?.message ?? 'Failed to load upcoming jobs'}
            onRetry={() => query.refetch()}
          />
        )}
        {query.data && query.data.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
            <CalendarDays className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No upcoming jobs</p>
          </div>
        )}
        {query.data && query.data.length > 0 && (
          <ul className="space-y-1.5">
            {query.data.slice(0, 5).map((job) => (
              <li key={job.id}>
                <button
                  type="button"
                  className="w-full rounded-md px-3 py-2 text-left transition-colors hover:bg-muted active:bg-muted"
                  onClick={() =>
                    job.jobId
                      ? void router.navigate({
                          to: '/dashboard/jobs/$id/appointments/$appointmentId',
                          params: { id: job.jobId, appointmentId: job.appointmentId ?? job.id },
                        })
                      : void router.navigate({
                          to: '/dashboard/bookings/$bookingId',
                          params: { bookingId: job.id },
                        })
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{job.customerName}</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span className="truncate">{job.service}</span>
                        <span>·</span>
                        <span className="shrink-0 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {/* §15 — a day-bound visit is anchored at midnight; show
                              its DAY only (a fabricated "12:00 AM" would mislead). */}
                          {formatDateTimeInTimeZone(
                            job.scheduledAt,
                            timeZone,
                            job.dayBound
                              ? { month: 'short', day: 'numeric' }
                              : {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                },
                          )}
                        </span>
                      </div>
                    </div>
                    <Badge
                      variant={statusVariant(job.status)}
                      className="shrink-0 capitalize text-[11px]"
                    >
                      {job.status}
                    </Badge>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Today's Tasks ───────────────────────────────────────────────────────────

function TodayTasks() {
  const query = useTodayTasks();

  return (
    <Card data-testid="widget-today-tasks">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-muted-foreground" />
          Today's Tasks
        </CardTitle>
      </CardHeader>
      <CardContent>
        {query.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-14 rounded-full" />
              </div>
            ))}
          </div>
        )}
        {query.isError && (
          <WidgetError message="Failed to load today's tasks" onRetry={() => query.refetch()} />
        )}
        {query.data && query.data.tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
            <CheckSquare className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No tasks due today</p>
          </div>
        )}
        {query.data && query.data.tasks.length > 0 && (
          <ul className="space-y-1.5">
            {query.data.tasks.slice(0, 5).map((task) => {
              const complete = isTaskComplete(task.status);
              const overdue = isTaskOverdue(task);
              const workflowStatus = taskWorkflowStatus(task.status);

              return (
                <li
                  key={task.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted transition-colors"
                >
                  <div
                    className={`h-2.5 w-2.5 rounded-full shrink-0 ${TASK_STATUS_DOT_CLASS[workflowStatus]}`}
                  />
                  <span
                    className={`flex-1 text-sm truncate ${complete ? 'line-through text-muted-foreground' : ''}`}
                  >
                    {task.title}
                  </span>
                  {overdue && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                      Overdue
                    </Badge>
                  )}
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${TASK_PRIORITY_BADGE_CLASS[task.priority]}`}
                  >
                    {TASK_PRIORITY_LABELS[task.priority]}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Operational KPI tiles (subset shown without reports:view) ────────────────

function fmtCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}
function fmtInt(n: number) {
  return new Intl.NumberFormat('en-US').format(n);
}

function OperationalTiles({
  filter,
  statsQuery,
  pipelineQuery,
}: {
  filter: DateFilter;
  statsQuery: UseQueryResult<DashboardStats, ApiError>;
  pipelineQuery: UseQueryResult<PipelineStats, ApiError>;
}) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const s = statsQuery.data;
  const p = pipelineQuery.data;
  const jobs = filter.period === 'today' ? s?.jobsToday : s?.jobsThisWeek;

  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 md:gap-4',
        pricingHidden ? 'md:grid-cols-2' : 'md:grid-cols-4',
      )}
    >
      <DashboardKpi
        label="Jobs"
        value={fmtInt(jobs?.count ?? 0)}
        isLoading={statsQuery.isLoading}
        delta={{ pct: jobs?.delta != null ? jobs.delta / 100 : null }}
      />
      <DashboardKpi
        label="Estimates"
        value={`${p?.pipeline.totalEstimates ?? 0} · ${p?.pipeline.awaitingApproval ?? 0} pending`}
        isLoading={pipelineQuery.isLoading}
      />
      {!pricingHidden ? (
        <>
          <DashboardKpi
            label="Invoices"
            value={`${s?.openInvoices.count ?? 0} · ${fmtCurrency(s?.openInvoices.totalAmount ?? 0)}`}
            isLoading={statsQuery.isLoading}
            delta={{ pct: s?.openInvoices.delta != null ? s.openInvoices.delta / 100 : null }}
          />
          <DashboardKpi
            label="Revenue"
            value={fmtCurrency(s?.revenueThisMonth.amount ?? 0)}
            isLoading={statsQuery.isLoading}
            delta={{
              pct: s?.revenueThisMonth.delta != null ? s.revenueThisMonth.delta / 100 : null,
            }}
          />
        </>
      ) : null}
    </div>
  );
}

// ─── Page Layout ─────────────────────────────────────────────────────────────

function DashboardHomePage() {
  const canViewBusiness = useHasPermission('reports:view');
  const pricingHidden = useIsFieldHidden('PRICING');
  const timeZone = useCompanyTimezone();
  const email = useAuthEmail();
  const { data: myProfile } = useMyProfile();
  const nowIso = new Date().toISOString();
  const todayKey = dateKeyInTimeZone(nowIso, timeZone);
  const [filter, setFilterState] = useState<DateFilter>(() => homeFilterFromSearch(todayKey));
  const setFilter = useCallback(
    (next: DateFilter) => {
      setFilterState(next);
      persistHomeFilter(next, todayKey);
    },
    [todayKey],
  );
  useEffect(() => persistHomeFilter(filter, todayKey), [filter, todayKey]);
  const statsQuery = useDashboardStats(filter);
  const pipelineQuery = usePipelineStats(filter);
  const weekday = formatDateTimeInTimeZone(nowIso, timeZone, { weekday: 'long' });
  const monthAndDay = formatDateTimeInTimeZone(nowIso, timeZone, {
    month: 'long',
    day: 'numeric',
  });
  const time = formatDateTimeInTimeZone(nowIso, timeZone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const hour = Number(
    formatDateTimeInTimeZone(nowIso, timeZone, { hour: 'numeric', hour12: false }),
  );
  const firstName = greetingFirstName({
    firstName: myProfile?.firstName,
    displayName: myProfile?.displayName,
    email,
  });
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const dateMeta = `${weekday} · ${monthAndDay} · ${time}`;

  useDashboardHeader(
    {
      context: (
        <div className="hidden min-w-0 items-center gap-3 sm:flex">
          <DashboardViewToggle />
          <div className="min-w-0 leading-none">
            <p className="mb-0.5 truncate font-mono text-[9.5px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
              {dateMeta}
            </p>
            <h1 className="truncate text-base font-medium leading-tight tracking-[-0.02em]">
              {greeting}
              {firstName ? `, ${firstName}` : ''}
            </h1>
          </div>
        </div>
      ),
      actions: <HomePeriodControl filter={filter} todayKey={todayKey} onChange={setFilter} />,
    },
    // canViewBusiness gates the DashboardViewToggle in this slot; re-register the
    // header node if reports:view resolves/changes so its visibility can't go stale.
    [
      dateMeta,
      filter.endDate,
      filter.period,
      filter.startDate,
      firstName,
      greeting,
      todayKey,
      canViewBusiness,
    ],
  );

  // The Cockpit Home is entirely financial (pace, revenue, AR, KPIs), so a
  // PRICING-masked reports:view user keeps the pricing-safe operational Home.
  const showOwnerHome = canViewBusiness && !pricingHidden;

  return (
    <>
      <Fullbleed />
      <div
        className={cn(
          'h-full overflow-y-auto px-4 py-4 md:px-6 md:py-5',
          showOwnerHome ? 'flex min-h-0 flex-col' : 'space-y-6',
        )}
      >
        {/* The app bar is full of the period control + global actions on phones,
            so the Home ↔ Operations switch lives at the top of the content here. */}
        <div className="mb-4 shrink-0 sm:hidden">
          <DashboardViewToggle fullWidth />
        </div>
        <div
          data-testid="kpi-section"
          className={cn(showOwnerHome && 'flex min-h-0 flex-1 flex-col')}
        >
          {showOwnerHome ? (
            <OwnerHome filter={filter} />
          ) : (
            <div className="space-y-4 md:space-y-6">
              <OperationalTiles
                filter={filter}
                statsQuery={statsQuery}
                pipelineQuery={pipelineQuery}
              />
              {!pricingHidden ? (
                <Suspense fallback={<Skeleton className="h-72 w-full rounded-xl" />}>
                  <RevenueChart filter={filter} />
                </Suspense>
              ) : null}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <ActionNeeded />
                <UpcomingJobs />
                <TodayTasks />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
